import {
  STRIPE_CONNECT_REQUIRED_CAPABILITIES,
  STRIPE_EXTERNAL_ACCOUNT_REQUIREMENT,
} from './stripeConnect';
import { getPayoutFlowState } from './payoutFlow';

export const STRIPE_MISSING_REQUIREMENT_TYPES = {
  ACCOUNT_INFORMATION: 'accountInformation',
  ADDRESS: 'address',
  BUSINESS_DETAILS: 'businessDetails',
  EXTERNAL_ACCOUNT: 'externalAccount',
  IDENTITY_VERIFICATION: 'identityVerification',
  PAYOUT_CAPABILITY: 'payoutCapability',
  TAX_INFORMATION: 'taxInformation',
  TERMS_ACCEPTANCE: 'termsAcceptance',
};

export const getStripeAccountData = stripeAccount =>
  stripeAccount?.attributes?.stripeAccountData || null;

export const getStripeAccountId = ({
  currentUser = null,
  stripeAccount = null,
  stripeAccountData = null,
} = {}) => {
  const currentUserStripeAccount = currentUser?.stripeAccount || null;
  return (
    stripeAccount?.attributes?.stripeAccountId ||
    stripeAccount?.id?.uuid ||
    currentUserStripeAccount?.attributes?.stripeAccountId ||
    currentUserStripeAccount?.id?.uuid ||
    currentUser?.attributes?.stripeAccountId ||
    stripeAccountData?.stripeAccountId ||
    null
  );
};

export const hasStripeAccount = params => Boolean(getStripeAccountId(params));

const getRequirementList = (stripeAccountData, requirementType) => {
  const requirements = stripeAccountData?.requirements || {};
  const requirementList = requirements[requirementType];
  return Array.isArray(requirementList) ? requirementList : [];
};

const getFutureRequirementList = (stripeAccountData, requirementType) => {
  const futureRequirements = stripeAccountData?.future_requirements || {};
  const requirementList = futureRequirements[requirementType];
  return Array.isArray(requirementList) ? requirementList : [];
};

const uniq = values => [...new Set(values.filter(Boolean))];

export const getStripeRequirementKeys = stripeAccountData =>
  uniq([
    ...getRequirementList(stripeAccountData, 'currently_due'),
    ...getRequirementList(stripeAccountData, 'past_due'),
    ...getRequirementList(stripeAccountData, 'eventually_due'),
    ...getFutureRequirementList(stripeAccountData, 'currently_due'),
    ...getFutureRequirementList(stripeAccountData, 'eventually_due'),
  ]);

const getRequirementType = requirement => {
  const field = `${requirement || ''}`.toLowerCase();

  if (!field) {
    return null;
  }

  if (field === STRIPE_EXTERNAL_ACCOUNT_REQUIREMENT) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.EXTERNAL_ACCOUNT;
  } else if (field.includes('tos_acceptance')) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.TERMS_ACCEPTANCE;
  } else if (
    field.includes('tax') ||
    field.includes('ein') ||
    field.includes('tin') ||
    field.includes('ssn')
  ) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.TAX_INFORMATION;
  } else if (
    field.includes('address') ||
    field.includes('city') ||
    field.includes('state') ||
    field.includes('postal_code') ||
    field.includes('zip')
  ) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.ADDRESS;
  } else if (
    field.includes('verification') ||
    field.includes('document') ||
    field.includes('id_number') ||
    field.includes('dob') ||
    field.includes('first_name') ||
    field.includes('last_name') ||
    field.includes('phone') ||
    field.includes('email') ||
    field.includes('representative') ||
    field.includes('person_') ||
    field.includes('individual')
  ) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.IDENTITY_VERIFICATION;
  } else if (
    field.includes('business_profile') ||
    field.includes('company') ||
    field.includes('business') ||
    field.includes('mcc') ||
    field.includes('url') ||
    field.includes('product_description')
  ) {
    return STRIPE_MISSING_REQUIREMENT_TYPES.BUSINESS_DETAILS;
  }

  return STRIPE_MISSING_REQUIREMENT_TYPES.ACCOUNT_INFORMATION;
};

export const getBlockingStripeRequirements = stripeAccountData => ({
  currently_due: getRequirementList(stripeAccountData, 'currently_due'),
  past_due: getRequirementList(stripeAccountData, 'past_due'),
});

export const hasExternalAccountRequirement = stripeAccountData =>
  [
    getRequirementList(stripeAccountData, 'currently_due'),
    getRequirementList(stripeAccountData, 'past_due'),
    getRequirementList(stripeAccountData, 'eventually_due'),
    getFutureRequirementList(stripeAccountData, 'currently_due'),
    getFutureRequirementList(stripeAccountData, 'eventually_due'),
  ].some(requirements => requirements.includes(STRIPE_EXTERNAL_ACCOUNT_REQUIREMENT));

export const hasBlockingStripeRequirements = stripeAccountData => {
  const { currently_due, past_due } = getBlockingStripeRequirements(stripeAccountData);
  return (
    currently_due.length > 0 ||
    past_due.length > 0 ||
    Boolean(stripeAccountData?.requirements?.disabled_reason)
  );
};

export const hasInactiveRequiredCapabilities = stripeAccountData => {
  const capabilities = stripeAccountData?.capabilities || null;
  const hasCapabilityData =
    capabilities &&
    STRIPE_CONNECT_REQUIRED_CAPABILITIES.some(capability => capabilities[capability] != null);

  return hasCapabilityData
    ? STRIPE_CONNECT_REQUIRED_CAPABILITIES.some(
        capability => capabilities[capability] !== 'active'
      )
    : false;
};

export const getStripeMissingRequirementTypes = stripeAccountData => {
  if (!stripeAccountData) {
    return [];
  }

  const requirementTypes = getStripeRequirementKeys(stripeAccountData).map(getRequirementType);
  const payoutCapabilityDisabled =
    hasInactiveRequiredCapabilities(stripeAccountData) ||
    stripeAccountData?.charges_enabled === false ||
    stripeAccountData?.details_submitted === false ||
    stripeAccountData?.requirements?.disabled_reason ||
    stripeAccountData?.payouts_enabled === false;
  const hasActionableRequirement = requirementTypes.filter(Boolean).length > 0;

  return uniq([
    ...requirementTypes,
    payoutCapabilityDisabled && !hasActionableRequirement
      ? STRIPE_MISSING_REQUIREMENT_TYPES.PAYOUT_CAPABILITY
      : null,
  ]);
};

export const isStripePayoutIncomplete = stripeAccountData => {
  if (!stripeAccountData) {
    return true;
  }

  return (
    stripeAccountData.payouts_enabled !== true ||
    stripeAccountData.charges_enabled === false ||
    stripeAccountData.details_submitted === false ||
    hasBlockingStripeRequirements(stripeAccountData) ||
    hasExternalAccountRequirement(stripeAccountData) ||
    hasInactiveRequiredCapabilities(stripeAccountData)
  );
};

export const isStripePayoutReady = stripeAccountData =>
  !isStripePayoutIncomplete(stripeAccountData);

export const shouldShowMissingStripeInfoBanner = ({
  currentUser = null,
  stripeAccount = null,
  stripeAccountData = null,
  stripeAccountFetched = false,
  stripeAccountFetchInProgress = false,
  sellerContext = false,
  stripeRequired = true,
} = {}) => {
  if (!currentUser?.id || !sellerContext || !stripeRequired) {
    return false;
  }

  const payoutFlow = getPayoutFlowState(currentUser);
  if (payoutFlow.isPaypalConnected || payoutFlow.canSkipPayoutOnboarding) {
    return false;
  }

  const hasAccount = hasStripeAccount({ currentUser, stripeAccount, stripeAccountData });
  if (!hasAccount) {
    return true;
  }

  if (stripeAccountFetchInProgress) {
    return false;
  }

  const resolvedStripeAccountData =
    stripeAccountData ||
    getStripeAccountData(stripeAccount) ||
    getStripeAccountData(currentUser?.stripeAccount);

  if (!resolvedStripeAccountData && !stripeAccountFetched) {
    return false;
  }

  return isStripePayoutIncomplete(resolvedStripeAccountData);
};
